import React from 'react';
import * as Sentry from '@sentry/react';
import { Box, Typography, Button } from '@mui/material';

interface Props {
	children: React.ReactNode;
	fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

interface State {
	hasError: boolean;
	error: Error | null;
}

class ErrorBoundary extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error('Error caught by boundary:', error, errorInfo);
		Sentry.withScope((scope) => {
			scope.setExtras({
				componentStack: errorInfo.componentStack,
				errorBoundary: true,
			});
			Sentry.captureException(error);
		});
	}

	resetError = () => {
		this.setState({ hasError: false, error: null });
	};

	render() {
		if (this.state.hasError && this.state.error) {
			if (this.props.fallback) {
				const FallbackComponent = this.props.fallback;
				return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
			}

			return (
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					minHeight="100vh"
					p={3}
				>
					<Typography variant="h4" gutterBottom>
						Oops! Something went wrong
					</Typography>
					<Typography variant="body1" color="textSecondary" paragraph>
						We're sorry for the inconvenience. The error has been reported to our team.
					</Typography>
					<Button variant="contained" color="primary" onClick={this.resetError}>
						Try Again
					</Button>
				</Box>
			);
		}

		return this.props.children;
	}
}

export default Sentry.withErrorBoundary(ErrorBoundary, {
	fallback: ({ error, resetError }) => (
		<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" p={3}>
			<Typography variant="h4" gutterBottom>
				Oops! Something went wrong
			</Typography>
			<Typography variant="body1" color="textSecondary" paragraph>
				{error instanceof Error ? error.message : 'An unexpected error occurred'}
			</Typography>
			<Button variant="contained" color="primary" onClick={resetError}>
				Try Again
			</Button>
		</Box>
	),
	showDialog: false,
});
